import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import styles from "./session-init-modal.module.css";
import { Feature } from "#/types/message";

export type SessionInitModalProps = {
  visible: boolean;

  serialRequired: boolean;
  serialNumber: string;
  setSerialNumber: (value: string) => void;

  requiredFeatures: Feature[];

  selectedFeatureValues?: Record<string, string | boolean | null | undefined>;

  onSelectFeature: (feature_name: string, option_name: string) => Promise<void> | void;

  onContinue: () => Promise<void> | void;
  continueDisabled: boolean;

  loading?: boolean;
};

function toArray(opts: any): any[] {
  if (!opts) return [];
  if (Array.isArray(opts)) return opts;
  if (typeof opts === "object") return Object.values(opts);
  return [];
}

function getOptValue(opt: any): string {
  if (opt == null) return "";

  // Prefer option_name if it exists (matches backend "option_name")
  if (typeof opt?.option_name === "string" && opt.option_name.trim() !== "") {
    return opt.option_name;
  }

  if (typeof opt === "string" || typeof opt === "number" || typeof opt === "boolean") {
    return String(opt);
  }

  return String(opt?.option_id ?? opt?.value ?? opt?.id ?? opt?.key ?? "");
}

function getOptLabel(opt: any, language: string): string {
  if (opt == null) return "";

  const friendly = opt?.friendly_name;
  if (typeof friendly === "string" && friendly.trim() !== "") return friendly;

  const label = opt?.label;
  if (typeof label === "string" && label.trim() !== "") return label;

  const name = opt?.name;
  if (typeof name === "string" && name.trim() !== "") return name;

  if (typeof name === "object" && name) {
    const byLang = name?.[language];
    if (typeof byLang === "string" && byLang.trim() !== "") return byLang;
    const first = Object.values(name)[0];
    if (typeof first === "string" && first.trim() !== "") return first;
  }

  const fallback = getOptValue(opt);
  return fallback || "Unknown";
}

export const SessionInitModal: React.FC<SessionInitModalProps> = ({
  visible,
  serialRequired,
  serialNumber,
  setSerialNumber,
  requiredFeatures,
  selectedFeatureValues,
  onSelectFeature,
  onContinue,
  continueDisabled,
  loading = false,
}) => {
  const { t, i18n } = useTranslation();
  const serialInputRef = useRef<HTMLInputElement | null>(null);

  // Local selections that do NOT save until Continue is pressed
  const [localSelections, setLocalSelections] = useState<Record<string, string>>({});

  // Reset local selections each time the modal opens
  useEffect(() => {
    if (!visible) return;
    setLocalSelections({});
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    if (!serialRequired) return;

    setTimeout(() => serialInputRef.current?.focus(), 0);
  }, [visible, serialRequired]);

  // Build dropdown option lists once
  const featureOptions = useMemo(() => {
    const out: Record<string, { value: string; label: string }[]> = {};

    for (const feature of requiredFeatures) {
      const key = String((feature as any).feature_name ?? "");
      if (!key) continue;

      const rawOptions = toArray((feature as any).options);

      out[key] = rawOptions
        .map((opt) => ({
          value: getOptValue(opt),
          label: getOptLabel(opt, i18n.language),
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, i18n.language, { sensitivity: "base" })
        );
    }

    return out;
  }, [requiredFeatures, i18n.language]);

  const getDisplayedValue = useCallback(
    (key: string) => {
      // Priority:
      // 1) local (what user changed in this modal session)
      // 2) saved (from backend / parent state)
      // 3) first option shown
      const local = localSelections[key];
      if (local != null && local !== "") return local;

      const saved = selectedFeatureValues?.[key];
      if (saved != null && String(saved) !== "") return String(saved);

      return featureOptions[key]?.[0]?.value ?? "";
    },
    [localSelections, selectedFeatureValues, featureOptions]
  );

  const handleContinueClick = useCallback(async () => {
    if (loading) return;

    // Save all required feature selections (including defaults)
    for (const feature of requiredFeatures) {
      const key = String((feature as any).feature_name ?? "");
      if (!key) continue;

      const value = getDisplayedValue(key);
      if (!value) continue;

      await onSelectFeature(key, value);
    }

    await onContinue();
  }, [loading, requiredFeatures, getDisplayedValue, onSelectFeature, onContinue]);

  if (!visible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.title}>{t("setupRequired") || "Setup required"}</div>
        </div>

        <div className={styles.body}>
          {serialRequired && (
            <div className={styles.row}>
              <label className={styles.label}>
                {t("serialNumber") || "Machine serial number"}
              </label>

              <input
                ref={serialInputRef}
                type="text"
                className={styles.input}
                value={serialNumber}
                inputMode="numeric"
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/[^\d]/g, "");
                  setSerialNumber(digitsOnly);
                }}
                disabled={loading}
              />

              <div className={styles.hint}>
                {t("serialNumberHint") || "Digits only. Leave empty if unknown."}
              </div>
            </div>
          )}

          {requiredFeatures.map((feature) => {
            const key = String((feature as any).feature_name ?? "");
            if (!key) return null;

            const options = featureOptions[key] ?? [];
            const valueToShow = getDisplayedValue(key);

            return (
              <div key={key} className={styles.row}>
                <label className={styles.label}>{(feature as any).name ?? key}</label>

                <select
                  className={styles.select}
                  disabled={loading}
                  value={valueToShow}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLocalSelections((prev) => ({ ...prev, [key]: next }));
                  }}
                >
                  {options.map((opt, idx) => (
                    <option key={`${key}-${opt.value}-${idx}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleContinueClick}
            disabled={loading || continueDisabled}
          >
            {loading ? (t("saving") || "Saving…") : (t("continue") || "Continue")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionInitModal;
